import { Formik, Form, Field } from "formik";
import React, { useState } from "react";
import { styled } from "../../stitches.config";
import * as Yup from "yup";
import Link from "../../components/Link";

import { body, bodyLarge, h2 } from "../commonStyles/Fonts";

import CheckIcon from "../assets/icons/checkbox-white.svg";
import DashboardHeader from "../DashboardHeader";
import confirmIcon from "../../assets/img/confirmation.svg";
import mailIcon from "../../assets/img/mail-circle.svg";
import phoneIcon from "../../assets/img/phone-circle.svg";
import Button from "../atoms/Button";
import { proposeArticle } from "../../api/proposeArticle";
import CopyButton from "./CopyButton";

interface TopicFormValues {
  subject: string;
  message: string;
  terms: boolean;
}

const DashboardSuggestTopic: React.FC = () => {
  const [isSend, setIsSend] = useState(false);
  const initialValues: TopicFormValues = {
    subject: "",
    message: "",
    terms: false,
  };
  const validationSchema = Yup.object().shape({
    subject: Yup.string().required("Pole wymagane"),
    message: Yup.string().required("Pole wymagane"),
    terms: Yup.boolean()
      .required("Musisz zaakceptować warunki")
      .oneOf([true], "Musisz zaakceptować warunki."),
  });

  const handleSubmit = ({ subject, message, terms }: TopicFormValues) => {
    proposeArticle({ terms, topic: subject, message }).then(() =>
      setIsSend(true)
    );
  };
  return (
    <StyledDashboardWrapper id="zglos-temat">
      <h2 className={h2()}>Zgłoś temat</h2>
      <p className={bodyLarge()}>
        Jako zalogowany użytkownik_czka możesz decydować o czym będziemy pisać.
        Zgłoś swoją propozycje na nasz następny artykuł.
      </p>

      <DashboardBox>
        <DashboardBoxWrapper>
          {isSend ? (
            <>
              <Confirm src={confirmIcon} />
              <DashboardHeader
                description={
                  "Obserwuj uważnie sekcje BRID. Jeśli Twój temat zostanie zatwierdzony to zostanie niedługo poddany głosowaniu, a każdy głos się liczy!"
                }
                title={"Dziękujemy za zgłoszenie tematu"}
                btnText={""}
                btnActive={false}
              />
              <Button
                type="black"
                text="Zaproponuj kolejny temat"
                starPosition="none"
                onClick={() => setIsSend(false)}
              />
            </>
          ) : (
            <FormWrapper>
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}>
                {({ errors, touched }) => (
                  <Form className={body()}>
                    <label className={`${errors.subject && touched.subject && "show-error-input-text"}`} htmlFor="email">Temat artykułu</label>
                    <Field
                      type="text"
                      name="subject"
                      className={`${bodyLarge()} ${errors.subject && touched.subject && "show-error-input-border"}`}
                      placeholder="Wpisz swoją propozycje"
                    />
                    {errors.subject && touched.subject && <span className="validationError">{errors.subject}</span>}
                    <label className={`${errors.message && touched.message && "show-error-input-text"}`} htmlFor="password">Opis</label>
                    <Field
                      type="text"
                      as="textarea"
                      name="message"
                      className={`${bodyLarge()} ${errors.message && touched.message && "show-error-input-border"}`}
                      placeholder="Opisz dlaczego powinniśmy zając się właśnie tym tematem."
                      rows={5}
                    />
                    {errors.message && touched.message && <span className="validationError">{errors.message}</span>}
                    <div className="checkbox">
                      <Field type="checkbox" className={`${errors.terms && touched.terms && "show-error-checkbox-border"}`} name="terms" />
                      Akceptuje
                      <Link to="/info/regulamin">regulamin</Link> i <Link to="/info/polityka-prywatnosci">politykę prywatności</Link>
                    </div>
                    {errors.terms && touched.terms && <span className="validationError">{errors.terms}</span>}
                    <Field className={bodyLarge()} type="submit" value="Wyślij" />
                  </Form>)}
              </Formik>
            </FormWrapper>
          )}
        </DashboardBoxWrapper>
      </DashboardBox>
      <DashboardBox>
        <DashboardHeader
          description={'Masz jakieś pytania do redakcji? Śmiało do nas napisz!'}
          title={"Skontaktuj się z redakcją"}
          btnText={""}
          btnActive={false}
        />
        <DashboardBoxWrapper>
          <ContactWrapper>
            <div className="copy-button-wrap">
              <img src={mailIcon} />
              <CopyButton text={"kontakt@natemat.pl"} />
            </div>
            <a href="tel:.881 666 627">
              <img src={phoneIcon} />
              <span className={bodyLarge()}>881 666 627</span>
            </a>
          </ContactWrapper>
        </DashboardBoxWrapper>
        <ExtraText className={bodyLarge()}>Jeśli masz problem z kontem "Twoje naTemat" lub pytania techniczne napisz do nas na pomoc@natemat.pl</ExtraText>
        <DashboardBoxWrapper>
          <ContactWrapper>
            <div className="copy-button-wrap">
              <img src={mailIcon} />
              <CopyButton text={"pomoc@natemat.pl"} />
            </div>
          </ContactWrapper>
        </DashboardBoxWrapper>
      </DashboardBox>
    </StyledDashboardWrapper>
  );
};

const StyledDashboardWrapper = styled("div", {
  marginBottom: "32px",
  "@bp4": {
    marginBottom: "80px",
  },
  "& h2": {
    color: "$grayscale90",
    margin: "0 16px 16px 16px",
    "@bp4": {
      margin: "0 0 16px 0",
    },
  },
  "& p": {
    margin: "0 16px 24px 16px",
    color: "$grayscale75",
    "@bp4": {
      margin: "0 0 24px 0",
    },
  },
});

const DashboardBox = styled("div", {
  background: "$grayscale0",
  borderBottom: "1px solid $grayscale10",
  padding: "0 16px 20px 16px",
  display: "flex",
  flexDirection: "column",
  "@bp4": {
    padding: "0 32px 12px 32px",
    marginBottom: "16px",
    borderBottom: "0px solid $grayscale10",
  },
});

const DashboardBoxWrapper = styled("div", {
  width: "100%",
  "& div": {
    width: "100%",
  },
  "& button": {
    marginBottom: "32px",
  },
});

const ExtraText = styled("div", {
  margin: "0 0 24px 0",
  width: "100%",
  color: "$grayscale75 !important",
});

const FormWrapper = styled("div", {
  color: "$grayscale90",
  "@bp2": {
    width: "auto",
  },
  "@bp4": {
    width: "85% !important",
  },
  label: {
    color: "$grayscale90",
  },
  "& form": {
    display: "flex",
    flexDirection: "column",
    "& label": {
      margin: "24px 0 4px 0",
    },
    "& input, & textarea": {
      resize: "none",
      border: "1px solid $grayscale25",
      fontWeight: "500",
      padding: "12px 14px",
      transition: "0.2s",
      caretColor: "$primary",
      "&::placeholder": {
        color: "$grayscale50",
      },
      "&:hover": {
        border: "1px solid $grayscale50",
      },
      "&:focus": {
        border: "1px solid $grayscale50",
        outline: "none",
      },
    },
    "& input[type = submit]": {
      color: "$grayscale0",
      borderWidth: "0px",
      background: "$grayscale100",
      marginBottom: "20px",
      marginTop: "24px",
      "@bp4": {
        width: "170px",
        marginTop: '28px'
      },
    },
  },
  "& .checkbox": {
    margin: "24px 0 0 0",
    display: "flex",
    alignItems: "center",
    paddingBottom: "0px",
    "& a": {
      color: "$grayscale100",
      margin: "0 3px",
    }
  },
  "& input[type = checkbox]": {
    marginRight: "8px",
    borderRadius: "0px",
    width: "16px",
    height: "16px",
    position: "relative",
    "&::before": {
      borderRadius: "0px",
      width: "16px",
      height: "16px",
      position: "absolute",
      border: "1px solid $grayscale50",
      content: "",
      right: "0px",
      top: "0px",
      display: "flex",
      color: "green",
      background: "$grayscale0",
      // transition: "0.2s",
    },
    "&:checked": {
      "&::before": {
        backgroundColor: "$primary",
        backgroundImage: `url("${CheckIcon}")`,
        backgroundPosition: "center",
        backgroundSize: "12px",
        backgroundRepeat: "no-repeat",
        border: "1px solid $primary",
      },
    },
  },
  "& .validationError": {
    color: "$aszdziennikPrimary",
    fontSize: "12px",
    lineHeight: "16px",
    marginTop: "8px",
  },
  ".show-error-input-border": {
    border: "1px solid $aszdziennikPrimary",
    "&:hover": {
      border: "1px solid $aszdziennikPrimary",
    },
    "&:focus": {
      border: "1px solid $aszdziennikPrimary",
    },
  },
  ".show-error-input-text": {
    color: "$aszdziennikPrimary",
  },
  ".show-error-checkbox-border": {
    "&::before": {
      border: "1px solid $aszdziennikPrimary !important",
    }
  }
});

const ContactWrapper = styled("div", {
  display: "flex",
  flexDirection: "column",
  width: "100%",
  "& img": {
    width: "40px",
    marginRight: "16px",
    flexShrink: 0,
  },
  "& a": {
    color: "$grayscale100",
    marginBottom: "20px",
  },
  "@bp4": {
    display: "grid",
    gridTemplateColumns: "43% 57%",
  },
  "& .copy-button-wrap": {
    color: "$grayscale100",
    marginBottom: "20px",
    display: "flex",
    alignItems: "center",
    "& button": {
      marginBottom: 0,
    }
  }
});

const Confirm = styled("img", {
  marginTop: "32px",
});

export default DashboardSuggestTopic;
